/**
 * Single source of truth for GPS data filters: order, visibility, and which
 * slice of the GPS context each one binds to.
 *
 * Layout rule (see getFilterLayout):
 *  - The base row holds the fixed controls (Période, Afficher, and Projet when
 *    not in jail mode) up to BASE_CAPACITY columns.
 *  - Any freed slot (e.g. Projet hidden in jail mode) is filled by promoting the
 *    first visible data filter(s) from this ordered list.
 *  - The remaining visible filters render in the advanced panel.
 *
 * `visible({ gps, jail })` preserves the "only show if relevant" notion
 * (e.g. Marques/Tiering only when there is more than one value).
 */
export const GPS_DATA_FILTERS = [
  // Marques is really important for L'Oréal customer. Should be first, and only shown if there is more than one brand in the project.
  { key: 'brands', label: 'Marques', optionsKey: 'brands', valueKey: 'selectedBrands', setterKey: 'setSelectedBrands', placeholder: 'Filtrer par marque...', visible: ({ gps }) => gps.issues.length > 1 && gps.totalBrands > 1 },
  { key: 'shopSigns', label: 'Enseignes', optionsKey: 'shopSigns', valueKey: 'selectedShopSigns', setterKey: 'setSelectedShopSigns', placeholder: 'Filtrer par enseigne...', enableSelectAll: true },
  { key: 'sectors', label: 'Secteurs', optionsKey: 'sectors', valueKey: 'selectedSectors', setterKey: 'setSelectedSectors', placeholder: 'Filtrer par secteur...', visible: ({ jail }) => !jail },
  { key: 'furnitures', label: 'Mobiliers', optionsKey: 'furnitures', valueKey: 'selectedFurnitures', setterKey: 'setSelectedFurnitures', placeholder: 'Filtrer par mobilier...' },
  { key: 'problems', label: 'Problèmes', optionsKey: 'problems', valueKey: 'selectedProblems', setterKey: 'setSelectedProblems', placeholder: 'Filtrer par problèmes...' },
  { key: 'status', label: 'Statuts', optionsKey: 'status', valueKey: 'selectedStatus', setterKey: 'setSelectedStatus', placeholder: 'Filtrer par statut...' },
  { key: 'tierings', label: 'Tiering', optionsKey: 'tierings', valueKey: 'selectedTierings', setterKey: 'setSelectedTierings', placeholder: 'Filtrer par tiering...', visible: ({ gps }) => gps.issues.length > 1 && gps.tierings.length > 1 },
  { key: 'requestTypes', label: 'Type de demande', optionsKey: 'requestTypes', valueKey: 'selectedRequestTypes', setterKey: 'setSelectedRequestTypes', placeholder: 'Filtrer par type de demande...' },
  { key: 'shops', label: 'Magasins', optionsKey: 'shops', valueKey: 'selectedShops', setterKey: 'setSelectedShops', placeholder: 'Filtrer par magasin...', enableSelectAll: true, optionLabel: (o) => o.enhancedLabel || o.label },
];

// Number of columns in the base row. Fixed controls = Afficher + Période
// (the Projet selector lives in the sidebar now), so the remaining slots are
// always free for promoted data filters — identical layout in jail and non-jail.
const BASE_CAPACITY = 4;
const FIXED_BASE_CONTROLS = 2; // Afficher + Période

export const getVisibleDataFilters = (gps, jail) =>
  GPS_DATA_FILTERS.filter((f) => (f.visible ? f.visible({ gps, jail }) : true));

/**
 * Split the visible data filters between the base row (promoted) and the
 * advanced panel, based on how many base slots the fixed controls leave free.
 */
export const getFilterLayout = (gps, jail) => {
  const visible = getVisibleDataFilters(gps, jail);
  const promoteCount = Math.max(0, BASE_CAPACITY - FIXED_BASE_CONTROLS);
  return {
    promoted: visible.slice(0, promoteCount),
    advanced: visible.slice(promoteCount),
  };
};
